import Phaser from 'phaser';
import { CANVAS_W, CANVAS_H, DEFAULT_STATS } from '../types';
import { loadAutosave, loadSlot, loadHighScore, loadNames, deleteSlot, clearAutosave, relativeTime } from '../systems/SaveManager';
import { DEFAULT_GAME_DATA, GameSceneData } from './GameScene';
import { getWorldTheme } from '../config/levels';
import { AudioManager } from '../systems/AudioManager';

interface MenuItem {
  label: () => string;
  onActivate: () => void;
  enabled: () => boolean;
}

export class MenuScene extends Phaser.Scene {
  private items: MenuItem[] = [];
  private cursor = 0;
  private texts: Phaser.GameObjects.Text[] = [];

  constructor() {
    super({ key: 'MenuScene' });
  }

  create() {
    this.cameras.main.setBackgroundColor('#131120');

    // Audio needs a user gesture to start — wire it up to first interaction.
    // Menu BGM = world 1 chiptune until a level kicks in.
    const initAudio = () => {
      AudioManager.init();
      AudioManager.startBgmForWorld(1);
    };
    this.input.once('pointerdown', initAudio);
    this.input.keyboard!.once('keydown', initAudio);

    // Decorative title
    const title = this.add.text(CANVAS_W / 2, 100, '💣 BOMBASTIK 💣', {
      fontFamily: 'monospace',
      fontSize: '54px',
      color: '#862d91',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.tweens.add({ targets: title, scale: { from: 1, to: 1.05 }, yoyo: true, repeat: -1, duration: 900 });

    this.add.text(CANVAS_W / 2, 160, 'Geo & Aris în salvarea Prințesei Elevarta', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#06d6a0',
    }).setOrigin(0.5);

    const names = loadNames();
    const auto = loadAutosave();
    const hs = loadHighScore();

    this.items = [
      {
        label: () => 'NOU — Joc nou',
        enabled: () => true,
        onActivate: () => {
          const fresh: GameSceneData = {
            ...DEFAULT_GAME_DATA,
            p1: { name: names.p1, stats: { ...DEFAULT_STATS }, lives: 3, score: 0 },
            p2: { name: names.p2, stats: { ...DEFAULT_STATS }, lives: 3, score: 0 },
          };
          this.scene.start('StoryScene', {
            kind: 'intro',
            next: { scene: 'GameScene', data: fresh },
          });
        },
      },
      {
        label: () => auto
          ? `CONTINUĂ — ${getWorldTheme(auto.data.world)} (${auto.data.world}-${auto.data.stage}) · ${relativeTime(auto.savedAt)}`
          : 'CONTINUĂ — fără save',
        enabled: () => !!auto,
        onActivate: () => {
          if (auto) this.scene.start('GameScene', auto.data);
        },
      },
      {
        label: () => {
          const s = loadSlot(1);
          return s ? `SLOT 1 — ${getWorldTheme(s.data.world)} (${s.data.world}-${s.data.stage}) · ${relativeTime(s.savedAt)}` : 'SLOT 1 — gol';
        },
        enabled: () => !!loadSlot(1),
        onActivate: () => { const s = loadSlot(1); if (s) this.scene.start('GameScene', s.data); },
      },
      {
        label: () => {
          const s = loadSlot(2);
          return s ? `SLOT 2 — ${getWorldTheme(s.data.world)} (${s.data.world}-${s.data.stage}) · ${relativeTime(s.savedAt)}` : 'SLOT 2 — gol';
        },
        enabled: () => !!loadSlot(2),
        onActivate: () => { const s = loadSlot(2); if (s) this.scene.start('GameScene', s.data); },
      },
      {
        label: () => {
          const s = loadSlot(3);
          return s ? `SLOT 3 — ${getWorldTheme(s.data.world)} (${s.data.world}-${s.data.stage}) · ${relativeTime(s.savedAt)}` : 'SLOT 3 — gol';
        },
        enabled: () => !!loadSlot(3),
        onActivate: () => { const s = loadSlot(3); if (s) this.scene.start('GameScene', s.data); },
      },
      {
        label: () => `NUME — ${names.p1} / ${names.p2}`,
        enabled: () => true,
        onActivate: () => { this.scene.start('NameInputScene'); },
      },
      {
        label: () => '⚙️  SETĂRI',
        enabled: () => true,
        onActivate: () => { this.scene.start('SettingsScene'); },
      },
    ];

    // Compact spacing — fits 6 items between subtitle and footer
    const startY = 200;
    const stepY = 38;
    this.texts = this.items.map((it, i) => {
      const y = startY + i * stepY;
      const t = this.add.text(CANVAS_W / 2, y, '', {
        fontFamily: 'monospace',
        fontSize: '18px',
      }).setOrigin(0.5);
      t.setInteractive({ useHandCursor: true });
      t.on('pointerover', () => { if (it.enabled()) { this.cursor = i; this.redraw(); } });
      t.on('pointerdown', () => { this.activate(i); });
      return t;
    });
    this.redraw();

    this.add.text(CANVAS_W / 2, CANVAS_H - 50,
      `🏆 High score: ${hs.toString().padStart(6, '0')}`,
      { fontFamily: 'monospace', fontSize: '14px', color: '#ffd166' }
    ).setOrigin(0.5);

    this.add.text(CANVAS_W / 2, CANVAS_H - 24,
      'P1: WASD+Q+Tab  P2: Săgeți+Enter+Shift  ESC pauză  D = șterge slot selectat',
      { fontFamily: 'monospace', fontSize: '10px', color: '#888' }
    ).setOrigin(0.5);

    // Robust grace: real-time gate. Refuses input for the first 400ms so a
    // stale ENTER/SPACE from the previous scene (Victory, Story) cannot
    // auto-activate the first item.
    const readyAt = Date.now() + 400;
    const gateOK = () => Date.now() >= readyAt;

    // Snap cursor to the first ENABLED item so users never start on a greyed
    // entry (e.g. "Continue" when there's no autosave).
    if (!this.items[this.cursor]?.enabled()) {
      const firstEnabled = this.items.findIndex(it => it.enabled());
      if (firstEnabled >= 0) { this.cursor = firstEnabled; this.redraw(); }
    }

    // Step the cursor by `dir` and SKIP disabled items so the user never gets
    // stuck on a greyed entry. Wraps around the list.
    const step = (dir: 1 | -1) => {
      const n = this.items.length;
      for (let tries = 0; tries < n; tries++) {
        this.cursor = (this.cursor + dir + n) % n;
        if (this.items[this.cursor].enabled()) return true;
      }
      return false;
    };

    const kb = this.input.keyboard!;
    const moveDown = () => { if (!gateOK()) return; if (step(1))  { AudioManager.sfx('menu-move'); this.redraw(); } };
    const moveUp   = () => { if (!gateOK()) return; if (step(-1)) { AudioManager.sfx('menu-move'); this.redraw(); } };
    const activate = () => { if (!gateOK()) return; this.activate(this.cursor); };
    kb.on('keydown-DOWN',  moveDown);
    kb.on('keydown-UP',    moveUp);
    kb.on('keydown-S',     moveDown);
    kb.on('keydown-W',     moveUp);
    kb.on('keydown-ENTER', activate);
    kb.on('keydown-SPACE', activate);
    kb.on('keydown-Q',     activate);
    // D = delete selected slot or autosave
    kb.on('keydown-D', () => {
      if (!gateOK()) return;
      if (this.cursor === 1 && auto) { // CONTINUĂ = autosave
        clearAutosave(); AudioManager.sfx('menu-select'); this.scene.restart();
      } else if (this.cursor >= 2 && this.cursor <= 4) {
        const slot = this.cursor - 1 as 1 | 2 | 3;
        if (loadSlot(slot)) { deleteSlot(slot); AudioManager.sfx('menu-select'); this.scene.restart(); }
      }
    });
    // Mute toggle on M
    kb.on('keydown-M', () => { const m = AudioManager.toggleMute(); console.log(m ? 'muted' : 'unmuted'); });
  }

  private redraw() {
    this.items.forEach((it, i) => {
      const t = this.texts[i];
      const selected = i === this.cursor;
      const enabled = it.enabled();
      const prefix = selected ? '▶ ' : '  ';
      const suffix = selected ? ' ◀' : '  ';
      t.setText(`${prefix}${it.label()}${suffix}`);
      t.setColor(enabled ? (selected ? '#ffd166' : '#fff') : '#555');
    });
  }

  private activate(i: number) {
    const it = this.items[i];
    if (!it.enabled()) return;
    AudioManager.sfx('menu-select');
    it.onActivate();
  }
}
